import { AlertDialog, Button, Spinner } from "@heroui/react";
import { useState } from "react";

import { useTranslations } from "@/src/i18n/i18n";
import type { Translations } from "@/src/i18n/translations";
import type { Report, ReportOutcome } from "@/src/pages/Reports/reports.type";
import type { SanctionKind } from "@/src/pages/Reports/sanction";
import { sanctionTarget } from "@/src/pages/Reports/sanction";

type ReportsKey = keyof Translations["reports"];

/** Nature de la cible → libellé du bouton, puis phrase de confirmation. */
const DELETE_KEY = {
  message: "delete-message",
  event: "delete-event",
  account: "delete-account",
  post: "delete-post",
  comment: "delete-comment",
} as const satisfies Record<SanctionKind, ReportsKey>;

const CONFIRM_KEY = {
  message: "confirm-message",
  event: "confirm-event",
  account: "confirm-account",
  post: "confirm-post",
  comment: "confirm-comment",
} as const satisfies Record<SanctionKind, ReportsKey>;

type ReportActionsProps = {
  report: Report;
  /** Vrai pendant que **ce** verdict part vers le serveur. */
  isResolving: boolean;
  /** Vrai pendant que **cette** suppression part vers le serveur. */
  isSanctioning: boolean;
  /** La cible a déjà été supprimée **pendant cette session**. */
  isSanctioned: boolean;
  onResolve: (_id: number, _outcome: ReportOutcome) => void;
  onSanction: (_report: Report) => void;
};

/**
 * Tout ce qu'on peut faire d'un signalement, sur une seule ligne.
 *
 * Les deux registres restent distincts — un verdict se requalifie, une
 * suppression non — mais ils ne sont plus deux sections empilées avec chacune
 * son intitulé et son paragraphe : trois boutons ne demandent pas deux
 * en-têtes. Ce qui les sépare est désormais **la place et le poids**, pas un
 * filet :
 *
 * - la suppression est à gauche, à l'opposé du geste courant, en rouge **sans
 *   fond** — visible pour qui la cherche, jamais sur le trajet du pouce ;
 * - les deux verdicts sont à droite, dans l'ordre où la décision se prend, et
 *   « Retenir » seul porte l'accent.
 *
 * Le rouge plein est gardé pour le bouton de confirmation de la boîte de
 * dialogue : c'est là, et seulement là, que le clic est irréversible.
 *
 * ⚠️ **Pas de retour en attente** : `PATCH /moderation/reports/:id` n'accepte
 * que `actioned` et `dismissed`. Un signalement clos ne propose donc qu'un
 * seul bouton, celui de l'**autre** verdict.
 */
export function ReportActions({
  report,
  isResolving,
  isSanctioning,
  isSanctioned,
  onResolve,
  onSanction,
}: ReportActionsProps) {
  const t = useTranslations("reports");
  const [isConfirming, setConfirming] = useState(false);

  const target = sanctionTarget(report);
  const isPending = report.status === "pending";

  // L'autre verdict : celui que la requalification propose.
  const opposite: ReportOutcome =
    report.status === "actioned" ? "dismissed" : "actioned";

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-separator pt-4">
      {/*
        La suppression garde sa place même quand elle est impossible : la
        phrase qui la remplace dit *pourquoi* il n'y a rien à supprimer, là où
        l'œil est allé la chercher.

        Sous 480 px la barre se replie et cette moitié passe au-dessus des
        verdicts. C'est l'ordre du DOM, pas un `order` CSS : au clavier, la
        tabulation suivrait sinon un chemin que l'écran contredit.
      */}
      {target === null ? (
        <p className="mr-auto text-xs text-pretty text-muted">
          {t("sanction-none")}
        </p>
      ) : (
        <AlertDialog isOpen={isConfirming} onOpenChange={setConfirming}>
          <Button
            className="mr-auto text-danger"
            // Deux raisons de refuser le clic : la cible est déjà supprimée,
            // ou la requête est en vol.
            isDisabled={isSanctioned || isSanctioning}
            size="sm"
            variant="ghost"
          >
            {isSanctioning ? <Spinner color="current" size="sm" /> : null}
            {isSanctioned
              ? t("sanction-deleted")
              : isSanctioning
                ? t("sanctioning")
                : t(DELETE_KEY[target.kind])}
          </Button>

          <AlertDialog.Backdrop>
            <AlertDialog.Container>
              {/* `role` explicite : la primitive de HeroUI rend un `dialog`
                  ordinaire. Une confirmation de suppression interrompt et
                  exige une réponse — c'est la définition d'un `alertdialog`,
                  que les lecteurs d'écran annoncent autrement. */}
              <AlertDialog.Dialog role="alertdialog">
                <AlertDialog.Header>
                  <AlertDialog.Heading>
                    {t("confirm-title")}
                  </AlertDialog.Heading>
                </AlertDialog.Header>

                {/*
                  ⚠️ La confirmation **nomme** ce qu'elle va détruire. Pour un
                  compte, « le compte 55 » n'est vérifiable par personne : le
                  pseudo, si. Les deux jetons sont passés ensemble, chaque
                  phrase prend celui qui la concerne — un jeton sans valeur
                  reste visible à l'écran (cf. `interpolate`), donc l'oubli se
                  verrait.
                */}
                <AlertDialog.Body className="text-pretty text-muted">
                  {t(CONFIRM_KEY[target.kind], {
                    id: target.id,
                    name: report.targetAccount?.username ?? `#${target.id}`,
                  })}
                </AlertDialog.Body>

                <AlertDialog.Footer className="gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onPress={() => setConfirming(false)}
                  >
                    {t("cancel")}
                  </Button>

                  <Button
                    size="sm"
                    variant="danger"
                    onPress={() => {
                      setConfirming(false);
                      onSanction(report);
                    }}
                  >
                    {t("confirm")}
                  </Button>
                </AlertDialog.Footer>
              </AlertDialog.Dialog>
            </AlertDialog.Container>
          </AlertDialog.Backdrop>
        </AlertDialog>
      )}

      <div className="flex items-center gap-2">
        {isPending ? (
          <>
            <Button
              isDisabled={isResolving}
              size="sm"
              variant="ghost"
              onPress={() => onResolve(report.id, "dismissed")}
            >
              {t("dismiss")}
            </Button>

            {/* `color="current"` : la couleur par défaut du `Spinner` est
                `accent`, invisible sur un bouton plein (cf. § HeroUI). */}
            <Button
              isDisabled={isResolving}
              size="sm"
              variant="primary"
              onPress={() => onResolve(report.id, "actioned")}
            >
              {isResolving ? <Spinner color="current" size="sm" /> : null}
              {isResolving ? t("resolving") : t("uphold")}
            </Button>
          </>
        ) : (
          <Button
            isDisabled={isResolving}
            size="sm"
            variant="ghost"
            onPress={() => onResolve(report.id, opposite)}
          >
            {isResolving ? <Spinner color="current" size="sm" /> : null}
            {isResolving
              ? t("resolving")
              : t(
                  opposite === "actioned"
                    ? "requalify-to-actioned"
                    : "requalify-to-dismissed",
                )}
          </Button>
        )}
      </div>
    </div>
  );
}
